/** Logic for the portal notification centre.
 *
 * The feed comes from `GET /api/portal/notifications/`, which serves this
 * contact's rows and their unread count in one response — the bell needs both
 * and a second request would only let them disagree.
 *
 * Notification state is not email delivery state: a row appears here the
 * moment the event happens, even when the email was suppressed after a hard
 * bounce or held until the end of quiet hours. See `core.Notification`.
 *
 * Pure functions, no React, no fetching — see notifications.test.ts.
 */

export interface PortalNotification {
  id: number
  kind: string
  kind_label: string
  title: string
  body: string
  /** Null when the notification is not about a ticket, or the ticket is gone. */
  ticket_ref: string | null
  ticket_title: string | null
  is_read: boolean
  read_at: string | null
  created_at: string
}

export interface NotificationFeed {
  results: PortalNotification[]
  unread: number
}

/** Where a row links to. Null when there is nothing to open — the row still
 *  renders, it simply is not a link. */
export function targetOf(notification: PortalNotification): string | null {
  return notification.ticket_ref ? `/tickets/${notification.ticket_ref}` : null
}

/** The badge caps at 99+; past that the exact number stops being information
 *  and starts being noise. Zero returns null so the caller renders no badge
 *  at all rather than a "0". */
export function badgeCount(unread: number): string | null {
  if (!Number.isFinite(unread) || unread <= 0) return null
  return unread > 99 ? '99+' : String(unread)
}

/** Newest first, unread before read within the same day is deliberately NOT
 *  done: the server orders by creation and reordering client-side would make
 *  a row jump the moment it was marked read, under the reader's cursor. */
export function groupByDay(
  notifications: PortalNotification[],
  now: Date,
): { label: string; rows: PortalNotification[] }[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const groups = new Map<string, PortalNotification[]>()

  for (const row of notifications) {
    const at = new Date(row.created_at)
    const day = Number.isNaN(at.getTime())
      ? 'Earlier'
      : labelFor(new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime(), today)
    const bucket = groups.get(day)
    if (bucket) bucket.push(row)
    else groups.set(day, [row])
  }

  return [...groups.entries()].map(([label, rows]) => ({ label, rows }))
}

function labelFor(day: number, today: number): string {
  const dayMs = 24 * 60 * 60 * 1000
  if (day === today) return 'Today'
  if (day === today - dayMs) return 'Yesterday'
  return 'Earlier'
}

/* --------------------------------------------------------- preferences --- */

/** One person's answer to "send me this event, on this channel".
 *
 * `enabled` is what applies right now; `overridden` says whether a row is
 * behind it. The two are different facts and the screen shows both — "on,
 * because that is the default" is not the same as "on, because you chose it",
 * and only the second survives a change to the default.
 */
export interface NotificationPreference {
  kind: string
  kind_label: string
  channel: 'in_app' | 'email'
  channel_label: string
  enabled: boolean
  overridden: boolean
}

export interface PreferenceFeed {
  preferences: NotificationPreference[]
}

/** A preference change on the wire. `enabled: null` clears the override and
 *  returns the event to whatever the organisation's rule says — a state that
 *  cannot be reached by sending `true`, because `true` is itself an override. */
export interface PreferenceChange {
  kind: string
  channel: string
  enabled: boolean | null
}

export interface PreferenceRow {
  kind: string
  label: string
  channels: Record<string, NotificationPreference>
}

/** One row per event, both channels on it. The server sends a flat list
 *  because that is what it stores; a person reads by event. */
export function groupByKind(preferences: NotificationPreference[]): PreferenceRow[] {
  const rows = new Map<string, PreferenceRow>()
  for (const preference of preferences) {
    const existing = rows.get(preference.kind)
    if (existing) {
      existing.channels[preference.channel] = preference
    } else {
      rows.set(preference.kind, {
        kind: preference.kind,
        label: preference.kind_label,
        channels: { [preference.channel]: preference },
      })
    }
  }
  return [...rows.values()]
}

/** What to send after a toggle.
 *
 * Toggling *off* writes `false`. Toggling back *on* sends `null` rather than
 * `true` when the default is on: that clears the override instead of freezing
 * today's default into the account, so somebody who mutes and unmutes ends up
 * where they started rather than pinned to a value that no longer follows the
 * organisation's. `true` is only ever sent where it means something — nowhere
 * yet, since a preference can only narrow, and it is accepted by the API for
 * the day a default turns off.
 */
export function toggleTo(current: NotificationPreference, next: boolean): PreferenceChange {
  return {
    kind: current.kind,
    channel: current.channel,
    enabled: next ? null : false,
  }
}

/** Only what differs from what the server sent. Saving the whole set would
 *  turn every inherited answer into an explicit override on the first press. */
export function changedPreferences(
  draft: Record<string, boolean>,
  serverState: NotificationPreference[],
): PreferenceChange[] {
  const changes: PreferenceChange[] = []
  for (const preference of serverState) {
    const key = preferenceKey(preference.kind, preference.channel)
    if (!(key in draft)) continue
    if (draft[key] === preference.enabled) continue
    changes.push(toggleTo(preference, draft[key]))
  }
  return changes
}

export function preferenceKey(kind: string, channel: string): string {
  return `${kind}:${channel}`
}

/** How many events this person has muted, for the summary line. Counts the
 *  effective answer, not the row count: an override that says "on" has muted
 *  nothing. */
export function mutedCount(preferences: NotificationPreference[]): number {
  return preferences.filter((preference) => !preference.enabled).length
}

/** What to tell somebody when the preferences call fails.
 *
 * A 403 here is not "you are not allowed to have preferences" — the endpoint is
 * scoped to the session and every signed-in person may edit their own — it is a
 * session that has expired underneath the page. Saying "permission denied"
 * would send somebody to their administrator over an expired cookie, so the two
 * are distinguished.
 */
export function preferenceErrorMessage(status: number | undefined): string {
  if (status === 403 || status === 401) {
    return 'Your session has expired. Sign in again to change your notification preferences.'
  }
  if (status === 400) {
    return 'That preference could not be saved. Reload the page and try again.'
  }
  return 'Preferences could not be loaded. This does not affect the notifications you receive.'
}
